import { Button, Form, InputTextArea } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import { ReactElement } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { SpinnerLegacy } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { useEvents } from "../../../hooks";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderCancel } from "../../../hooks/api/v2/order/v2/useOrderCancel";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { isPaidCheckoutOrder } from "../../../utils/sellarHelpers";
import { ChatInput } from "../../../components/chatInput/ChatInput";

const supplierCancellationReasons = [
  "Product(s) no longer in stock",
  "I confirmed the order but now need to add/change some items",
  "I chose the wrong fulfillment type",
  "I issued a card payment link but the customer paid by other means",
  "I couldn’t deliver to the customer in time",
  "The customer has unpaid invoices",
  "Other",
];

const retailerCancellationReasons = [
  "I want different products now",
  "I’m getting products from somewhere else now",
  "The supplier took too long to respond",
  "I don't want this order",
  "I didn't request this order",
  "Other",
];
export function CancelOrderDialogContent({
  order,
  onDismiss,
  onSubmit,
}: {
  order: OrderGetResponseType;
  onDismiss: () => void;
  onSubmit?: () => void;
}): ReactElement {
  const { track } = useEvents();
  const { business } = useSellarCurrentBusiness();
  const cancellationReason = business?.Supplier
    ? supplierCancellationReasons
    : retailerCancellationReasons;
  const { handleSubmit, register, setValue, control } = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      cancellationReason: "",
      otherCancellationReason: "",
      comment: "",
    },
  });
  const cancellationReasonValue = useWatch({
    control,
    name: "cancellationReason",
  });
  const comment = useWatch({
    control,
    name: "comment",
  });

  const orderCancelMutation = useOrderCancel();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasPaidCheckoutOrder = isPaidCheckoutOrder({
    paidAt: order.paidAt,
    paymentType: order.paymentType,
  });
  const businessName = business?.Supplier
    ? order.Retailer.Business.name
    : order.Supplier.Business.name;
  const title = hasPaidCheckoutOrder
    ? "Are you sure you want to cancel & refund this order?"
    : "Are you sure you want to cancel this order?";
  const sharedDescription = `Let ${businessName} know why you are cancelling the order.`;
  const paidAmount =
    order.paymentType === "checkout"
      ? order.paidAmount - order.refundedAmount
      : order.price;
  const description = hasPaidCheckoutOrder
    ? `${sharedDescription} A total of ${formatCurrency(paidAmount)} will be refunded to the payment card used to purchase in 5-10 business days.`
    : sharedDescription;
  const loading = orderCancelMutation.isPending;
  const errorMessage = orderCancelMutation.isError
    ? (apiV2AssertError(orderCancelMutation.error)?.response?.data.message ??
      "We couldn't cancel this order. Please try again.")
    : null;

  const showFulfillmentWarning =
    business?.Supplier &&
    cancellationReasonValue === "I chose the wrong fulfillment type" &&
    order.confirmedAt &&
    !order.dispatchedAt;

  return (
    <>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={title}
        description={description}
      />
      <Form
        className="flex w-full flex-col"
        onSubmit={handleSubmit(async (data) => {
          // oxlint-disable-next-line react/purity -- Captured when this deferred submit handler runs.
          const cancelledAt = new Date();
          const cancelledBy = business?.Supplier ? "supplier" : "retailer";
          const cancellationReason =
            data.cancellationReason === "Other"
              ? data.otherCancellationReason
              : data.cancellationReason;
          if (!cancellationReason)
            throw new Error("Cancellation reason is required");

          orderCancelMutation.mutate(
            {
              id: order.id,
              data: {
                cancelledAt,
                cancelledBy,
                cancellationReason,
                orderComment: data.comment ? data.comment : null,
              },
            },
            {
              onSuccess: () => {
                track("Order Cancelled", {
                  initiator: cancelledBy,
                  orderId: order.id,
                  cancelledAt,
                  Reason: cancellationReason,
                  paymentType: order.paymentType,
                });
                onSubmit?.();
                onDismiss();
              },
            },
          );
        })}
      >
        <DialogContent className={"flex flex-col gap-2"}>
          {errorMessage && (
            <Alert className="w-full" type="error" message={errorMessage} />
          )}
          {orderCancelMutation.isPending ? (
            <SpinnerLegacy />
          ) : (
            <>
              {business?.Supplier &&
                cancellationReasonValue === "Product(s) no longer in stock" &&
                !order.confirmedAt &&
                !order.paidAt && (
                  <Alert
                    title={"You can edit this order instead of cancelling."}
                    message={
                      "If your customer is happy with a replacement for the out of stock product(s), you can exchange them by editing this order."
                    }
                    type={"information"}
                  >
                    <Button
                      color={"secondary"}
                      className={"border-blue-300 bg-blue-100 text-blue-800"}
                      onClick={() =>
                        navigate({
                          pathname: "edit",
                          search: searchParams.toString(),
                        })
                      }
                    >
                      Edit order
                    </Button>
                  </Alert>
                )}
              <InputLabel>Cancellation Reason</InputLabel>
              <InputContainer>
                <InputSelect
                  {...register("cancellationReason", {
                    required: true,
                    disabled: orderCancelMutation.isPending,
                  })}
                >
                  {cancellationReason.map((reason, index) => (
                    <InputOption key={index} value={reason}>
                      {reason}
                    </InputOption>
                  ))}
                </InputSelect>
              </InputContainer>
              {cancellationReasonValue === "Other" && (
                <>
                  <InputContainer>
                    <InputTextArea
                      variant="legacyFixed"
                      {...register("otherCancellationReason", {
                        required: true,
                      })}
                      maxLength={900}
                      placeholder={"Please specify"}
                    />
                  </InputContainer>
                </>
              )}
              <InputLabel>
                Add a comment for{" "}
                {business?.Supplier
                  ? order.Retailer.Business.name
                  : order.Supplier.Business.name}
              </InputLabel>
              <ChatInput
                {...register("comment", {
                  required: false,
                })}
                value={comment}
                setValue={(val) => setValue("comment", val)}
                placeholder={"Write your comment here..."}
                maxLength={900}
                rows={4}
                maxRows={8}
              />
              {showFulfillmentWarning && (
                <Alert
                  title={
                    "You can edit fulfillment type instead of cancelling this order"
                  }
                  message={
                    "Edit fulfillment type for orders up until they are marked as dispatched via the 'Edit' button in the delivery section"
                  }
                  type={"warning"}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogCTA>
          <Button disabled={loading} loading={loading} type="submit">
            Cancel Order
          </Button>
        </DialogCTA>
      </Form>
    </>
  );
}
